import {formatNumber} from '@/components/charts/numbers';
import {ActiveElement, Chart, ChartEvent, LegendItem, TooltipItem} from 'chart.js';

/**
 * The three views of the "what is going on in the portfolio" card, one
 * horizontal bar per estate: the value moves since the previous revaluation,
 * the appreciation since purchase against inflation, and the owner's equity
 * against the mortgage debt.
 */
export type InsightsView = 'moves' | 'appreciations' | 'mortgages';

export interface EstateMove {
    estimateId: string;
    label: string;
    change: number;
    changePercent: number;
    role: 'top' | 'brake' | null;
}

export interface EstateAppreciation {
    estimateId: string;
    label: string;
    appreciationPercent: number;
    inflationPercent: number | null;
}

export interface EstateMortgage {
    estimateId: string;
    label: string;
    equity: number;
    balance: number;
    fixationEnd: string | null;
    monthsToFixationEnd: number | null;
}

export interface InsightsChartData {
    moves: EstateMove[];
    appreciations: EstateAppreciation[];
    mortgages: EstateMortgage[];
}

const BAR_THICKNESS = 18;
const MUTED_OPACITY = 0.35;

export default class InsightsChart {
    private chart: Chart<'bar'>;

    private view: InsightsView;

    public constructor(
        private readonly element: HTMLCanvasElement,
        private readonly data: InsightsChartData,
        private readonly links: Record<string, string>,
        private readonly language: string,
        private readonly currency: string,
        view: InsightsView,
    ) {
        this.view = view;
        this.chart = new Chart<'bar'>(element, {
            type: 'bar',
            data: {labels: [], datasets: []},
            options: {
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                animation: {duration: 400},
                layout: {padding: {right: 8}},
                scales: {
                    x: {
                        beginAtZero: true,
                        grid: {
                            color: 'rgba(148, 163, 184, 0.18)',
                            borderDash: [4, 4],
                            drawBorder: false,
                        },
                        ticks: {
                            maxTicksLimit: 6,
                            callback: value => this.formatAxis(Number(value)),
                        },
                    },
                    y: {
                        stacked: true,
                        grid: {display: false, drawBorder: false},
                        ticks: {
                            autoSkip: false,
                            font: {size: 12},
                            callback: (_, index) => InsightsChart.shorten(String(this.chart.data.labels?.[index] ?? '')),
                        },
                    },
                },
                plugins: {
                    legend: {
                        display: false,
                        position: 'bottom',
                        onClick: () => undefined,
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false,
                        displayColors: false,
                        callbacks: {
                            title: items => (items.length > 0 ? String(items[0].label) : ''),
                            label: item => this.tooltipLines(item),
                        },
                    },
                },
                onHover: (event: ChartEvent, elements: ActiveElement[]) => {
                    const target = event.native?.target as HTMLElement | null;

                    if (target) {
                        target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                    }
                },
                onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
                    if (elements.length === 0) {
                        return;
                    }

                    const link = this.links[this.rows()[elements[0].index]?.estimateId];

                    if (link) {
                        window.location.href = link;
                    }
                },
            },
            plugins: [this.inflationMarkersPlugin()],
        });

        this.render();
    }

    public switchTo(view: InsightsView): void {
        this.view = view;
        this.render();
    }

    /** The height that fits every bar of the current view, for the canvas wrapper. */
    public preferredHeight(): number {
        return Math.max(120, this.rows().length * (BAR_THICKNESS + 14) + 48);
    }

    private rows(): Array<{estimateId: string}> {
        return this.data[this.view];
    }

    private render(): void {
        const {labels, datasets, legend} = this.build();

        this.chart.data.labels = labels;
        this.chart.data.datasets = datasets;

        const xScale = this.chart.options.scales?.x;

        if (xScale) {
            xScale.stacked = this.view === 'mortgages';
        }

        if (this.chart.options.plugins?.legend) {
            this.chart.options.plugins.legend.display = legend.length > 0;
            this.chart.options.plugins.legend.labels = {
                usePointStyle: true,
                boxWidth: 8,
                boxHeight: 8,
                padding: 16,
                generateLabels: () => legend,
            };
        }

        this.chart.update();
    }

    private build(): {labels: string[]; datasets: Chart<'bar'>['data']['datasets']; legend: LegendItem[]} {
        const success = InsightsChart.themeColor('--bs-success-rgb', '38, 191, 148');
        const danger = InsightsChart.themeColor('--bs-danger-rgb', '230, 83, 60');
        const primary = InsightsChart.themeColor('--primary-rgb', '242, 127, 36');
        const muted = '148, 163, 184';

        if (this.view === 'moves') {
            const moves = this.data.moves;

            return {
                labels: moves.map(move => move.label),
                datasets: [{
                    data: moves.map(move => move.change),
                    backgroundColor: moves.map(move => InsightsChart.rgba(
                        move.change < 0 ? danger : success,
                        move.role === null ? MUTED_OPACITY : 1,
                    )),
                    borderRadius: 4,
                    barThickness: BAR_THICKNESS,
                }],
                legend: [],
            };
        }

        if (this.view === 'appreciations') {
            const appreciations = this.data.appreciations;

            return {
                labels: appreciations.map(item => item.label),
                datasets: [{
                    data: appreciations.map(item => item.appreciationPercent),
                    backgroundColor: appreciations.map(item => InsightsChart.rgba(
                        item.appreciationPercent < 0 ? danger : success,
                        item.inflationPercent !== null && item.appreciationPercent <= item.inflationPercent
                            ? MUTED_OPACITY
                            : 1,
                    )),
                    borderRadius: 4,
                    barThickness: BAR_THICKNESS,
                }],
                legend: appreciations.some(item => item.inflationPercent !== null)
                    ? [{
                        text: 'Inflace za dobu držení',
                        pointStyle: 'line',
                        strokeStyle: InsightsChart.rgba('23, 28, 40', 0.7),
                        fillStyle: 'transparent',
                        lineDash: [3, 3],
                        lineWidth: 2,
                        hidden: false,
                        datasetIndex: -1,
                    }]
                    : [],
            };
        }

        const mortgages = this.data.mortgages;

        return {
            labels: mortgages.map(item => item.label),
            datasets: [
                {
                    label: 'Vlastní podíl',
                    data: mortgages.map(item => Math.max(0, item.equity)),
                    backgroundColor: InsightsChart.rgba(primary, 1),
                    borderRadius: {topLeft: 4, bottomLeft: 4},
                    borderSkipped: false,
                    barThickness: BAR_THICKNESS,
                },
                {
                    label: 'Dluh bance',
                    data: mortgages.map(item => item.balance),
                    backgroundColor: InsightsChart.rgba(muted, 0.55),
                    borderRadius: {topRight: 4, bottomRight: 4},
                    borderSkipped: false,
                    barThickness: BAR_THICKNESS,
                },
            ],
            legend: [
                {text: 'Vlastní podíl', pointStyle: 'circle', fillStyle: InsightsChart.rgba(primary, 1), strokeStyle: 'transparent', lineWidth: 0, hidden: false, datasetIndex: 0},
                {text: 'Dluh bance', pointStyle: 'circle', fillStyle: InsightsChart.rgba(muted, 0.55), strokeStyle: 'transparent', lineWidth: 0, hidden: false, datasetIndex: 1},
            ],
        };
    }

    private tooltipLines(item: TooltipItem<'bar'>): string[] {
        const index = item.dataIndex;

        if (this.view === 'moves') {
            const move = this.data.moves[index];

            return [
                `${InsightsChart.signed(this.formatMoney(move.change), move.change)} (${this.signedPercent(move.changePercent)})`,
                ...(move.role === 'top' ? ['Hybatel měsíce'] : move.role === 'brake' ? ['Brzda měsíce'] : []),
            ];
        }

        if (this.view === 'appreciations') {
            const appreciation = this.data.appreciations[index];
            const lines = [`Zhodnocení od nákupu ${this.signedPercent(appreciation.appreciationPercent)}`];

            if (appreciation.inflationPercent !== null) {
                lines.push(`Inflace za stejné období ${this.signedPercent(appreciation.inflationPercent)}`);
                lines.push(appreciation.appreciationPercent > appreciation.inflationPercent ? 'Poráží inflaci' : 'Zaostává za inflací');
            }

            return lines;
        }

        const mortgage = this.data.mortgages[index];

        if (item.datasetIndex === 1) {
            return [];
        }

        const total = mortgage.equity + mortgage.balance;
        const lines = [
            `Vlastní podíl ${this.formatMoney(mortgage.equity)}`,
            `Dluh bance ${this.formatMoney(mortgage.balance)}`
                + (total > 0 ? ` (LTV ${Math.round(mortgage.balance / total * 100)} %)` : ''),
        ];

        if (mortgage.fixationEnd !== null) {
            lines.push(`Konec fixace ${new Intl.DateTimeFormat(this.language, {month: '2-digit', year: 'numeric'}).format(new Date(mortgage.fixationEnd))}`);
        }

        return lines;
    }

    /**
     * A short dashed tick across every bar of the "since purchase" view at the
     * inflation over the estate's holding period — the bar beyond it is the
     * real gain.
     */
    private inflationMarkersPlugin() {
        return {
            id: 'inflationMarkers',
            afterDatasetsDraw: (chart: Chart<'bar'>) => {
                if (this.view !== 'appreciations') {
                    return;
                }

                const meta = chart.getDatasetMeta(0);
                const xScale = chart.scales.x;
                const context = chart.ctx;

                context.save();
                context.setLineDash([3, 3]);
                context.lineWidth = 2;
                context.strokeStyle = InsightsChart.rgba('23, 28, 40', 0.7);

                this.data.appreciations.forEach((item, index) => {
                    const bar = meta.data[index];

                    if (item.inflationPercent === null || !bar) {
                        return;
                    }

                    const x = xScale.getPixelForValue(item.inflationPercent);

                    context.beginPath();
                    context.moveTo(x, bar.y - BAR_THICKNESS / 2 - 4);
                    context.lineTo(x, bar.y + BAR_THICKNESS / 2 + 4);
                    context.stroke();
                });

                context.restore();
            },
        };
    }

    private formatAxis(value: number): string {
        if (this.view === 'appreciations') {
            return `${value} %`;
        }

        const absolute = Math.abs(value);
        const compact = absolute >= 1_000_000
            ? `${(absolute / 1_000_000).toLocaleString(this.language, {maximumFractionDigits: 1})} mil.`
            : absolute >= 1_000
                ? `${Math.round(absolute / 1_000)} tis.`
                : String(absolute);

        return value < 0 ? `−${compact}` : compact;
    }

    private formatMoney(value: number): string {
        return formatNumber(Math.abs(value), 'money', this.language, this.currency);
    }

    private static signed(formatted: string, value: number): string {
        return value < 0 ? `−${formatted}` : `+${formatted}`;
    }

    private signedPercent(value: number): string {
        const formatted = Math.abs(value).toLocaleString(this.language, {maximumFractionDigits: 1});

        return value < 0 ? `−${formatted} %` : `+${formatted} %`;
    }

    private static shorten(label: string): string {
        return label.length > 28 ? `${label.slice(0, 27)}…` : label;
    }

    private static themeColor(variable: string, fallback: string): string {
        const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

        return value !== '' ? value : fallback;
    }

    private static rgba(rgb: string, opacity: number): string {
        return `rgba(${rgb}, ${opacity})`;
    }
}
